import { Component, DoCheck, Input } from '@angular/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { AbstractShape } from '../../abstract/Shape';
import { Overview } from '../../abstract/Overview';
import { CommonModule } from '@angular/common';
import { ShapeRefArray } from '../../clases/ShapeRefArray';
import { MatInputModule } from '@angular/material/input';
import { ControlSlider } from '../../clases/ControlSlider';
import { ControlSliderComponent } from '../control-slider/control-slider.component';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { Dictionary } from '../../types/Dictionary';
import { IField } from '../../interfaces/Field';
import { nextTaskType, previusTaskDepencency } from '../../utils/SimulationUtils';
import { timezones as timezoneOptions } from '../../utils/Timezones';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SimulationValues } from '../../clases/SimulationValues';
import { DataViewer } from '../../abstract/DataViewer';
import { SimulationService } from '../../services/simulation.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  FullSimulationReport,
  SimulationArrivalScheduleShift,
  SimulationGlobalAverage,
  SimulationNodeBlock,
  SimulationOutputFile,
  SimulationResourceInterruption,
  SimulationResourceStructure,
  SimulationResourceStructureItem,
  SimulationReturnValues,
  SimulationSavedConfig,
  SimulationSendValues
} from '../../interfaces/Simulation';
import { ServerResponse, ServerResponseObject } from '../../interfaces/ServerResponse';
import { DataViewerComponent } from '../tab-models/data-viewer/data-viewer.component';
import { SimulationJson } from '../../../../public/simulation-json';
import { MatDialog } from '@angular/material/dialog';
import { NotifyDialogComponent } from '../dialogs/notify-dialog/notify-dialog.component';
import { SimpleInputDialogComponent } from '../dialogs/simple-input-dialog/simple-input-dialog.component';
import { SimulationComponent } from '../tab-models/simulation/simulation.component';
import { SimulationReportDialogComponent } from '../dialogs/simulation-report-dialog/simulation-report-dialog.component';
import FileSaver from 'file-saver';
import { generateCurrentDate, generateUUID } from '../../utils/utils';
import { SimulationStepPanelComponent } from './simulation-step-panel/simulation-step-panel.component';
import { CanvasItem } from '../../clases/CanvasItem';
import { AgentSimulationLastRun, buildAgentSimulationResultDigest, simulationConfigurationFingerprint } from '../../agent/simulation-result-digest';

type SimulationStepDirection = 'previous' | 'next';
type ArrivalRateTimeUnit = 'seconds' | 'minutes' | 'hours';

interface SimulationStepRelations {
  previousAutoNodeId: string | null;
  nextAutoNodeId: string | null;
  previousAutoHidden: boolean;
  nextAutoHidden: boolean;
  previousManualNodeIds: string[];
  nextManualNodeIds: string[];
  selectedPreviousNodeId: string | null;
  selectedNextNodeId: string | null;
}

interface SimulationArrivalRateEntry {
  nodeId: string | null;
  arrivals: number;
  intervalValue: number;
  intervalUnit: ArrivalRateTimeUnit;
}

interface EditableSimulationResourceStructure
  extends Omit<SimulationResourceStructure, 'items'> {
  items: SimulationResourceStructureItem[];
}

interface SavedSimulationConfiguration {
  model_UUID: string;
  simID: string;
  name: string;
  simCofig: SimulationSendValues;
}

interface ImportedSimulationConfiguration {
  model_UUID?: string;
  name?: string;
  simConfig?: SimulationSendValues;
}

interface ImportedSimulationResult extends FullSimulationReport {
  model_UUID: string;
  sim_send_values: SimulationSendValues;
}

interface SimulationTaskRelationValues {
  task_UUID: string;
  previous_task: number[];
  next_task: SimulationNodeBlock['next_task'];
}

export enum pTDEnum { AND, OR }
@Component({
  selector: 'simulation-overview',
  standalone: true,
  imports: [
    CommonModule,
    MatSliderModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    ControlSliderComponent,
    MatButtonModule,
    MatExpansionModule,
    DragDropModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinner,
    SimulationStepPanelComponent
  ],
  templateUrl: './simulation-overview.component.html',
  styleUrl: './simulation-overview.component.scss'
})
export class SimulationOverviewComponent extends Overview implements DoCheck {
  private static readonly fallbackTimezone = 'America/New_York';
  /** Monotonic, tab-local guard used by the Agent bridge. */
  simulationRevision = 0;
  simulationDirty = false;
  private lastPersistedSimulationConfig = '';
  private lastObservedSimulationConfig = '';

  constructor(
    private simulationService: SimulationService,
    private dialog: MatDialog
  ){super();}

  globalWorkerSlider: ControlSlider = new ControlSlider("Workers", "n°", 1, 10, 1, undefined, ()=>{
    let nodes = this.simulationNodes.getShape();
    for (const node of nodes) {
      if(node.simulationValues && !node.simulationValues.no_worker) 
        node.simulationValues.workersSlider.value = this.globalWorkerSlider.value;
    }
  });
  globalTimeSlider: ControlSlider = new ControlSlider("Time %", "%", -100, 100, 0, undefined, ()=>{
    let nodes = this.simulationNodes.getShape();
    for (const node of nodes) {
      if(node.simulationValues) 
        node.simulationValues.timeSlider.value = this.globalTimeSlider.value;
    }
  });
  globalWaitSlider: ControlSlider = new ControlSlider("Max Wait Time", "n°", 0, 100, 0, undefined, ()=>{
    let nodes = this.simulationNodes.getShape();
    for (const node of nodes) {
      if(node.simulationValues) 
        node.simulationValues.waitSlider.value = this.globalWaitSlider.value;
    }
  });

  workersSlider: ControlSlider = new ControlSlider("Workers", "n°", 1, 10, 1, undefined, ()=>{
    let nodes = this.currentShapeFocus.getShape();
    for (const node of nodes) {
      if(node.simulationValues && !node.simulationValues.no_worker) 
        node.simulationValues.workersSlider.value = this.workersSlider.value;
    }
  });
  timeSlider: ControlSlider = new ControlSlider("Time %", "%", -100, 100, 0, undefined, ()=>{
    let nodes = this.currentShapeFocus.getShape();
    for (const node of nodes) {
      if(node.simulationValues) 
        node.simulationValues.timeSlider.value = this.timeSlider.value;
    }
  });
  waitSlider: ControlSlider = new ControlSlider("Max Wait Time", "n°", 0, 100, 0, undefined, ()=>{
    let nodes = this.currentShapeFocus.getShape();
    for (const node of nodes) {
      if(node.simulationValues) 
        node.simulationValues.waitSlider.value = this.waitSlider.value;
    }
  });

  @Input() currentShapeFocus!: ShapeRefArray;
  @Input() dataViewer!: SimulationComponent;

  simulationNodes: ShapeRefArray = new ShapeRefArray(null);
  stepRelations: Record<string, SimulationStepRelations> = {};
  stepCodeByNodeId: Record<string, number> = {};

  private readonly simulationStepIncrement = 100;
  
  previusTaskDepencency = previusTaskDepencency;
  nextTaskType = nextTaskType;

  isLoading: boolean = false;
  simulationReturnValues: SimulationReturnValues | undefined;
  simulationReport: string | undefined;
  simulationFullResult: ImportedSimulationResult | undefined;
  private lastRun: AgentSimulationLastRun | undefined;
  simulationOutputFiles: SimulationOutputFile[] = [];

  simulationIterations = 10;
  simulationPeriodMinutes = 480;
  simulationResourcesStructure: EditableSimulationResourceStructure[] = [];
  simulationArrivalRateEntries: SimulationArrivalRateEntry[] = [];
  simulationSeed = 2;
  simulationStartDatetime = '2026-05-11T08:00';
  simulationTimezone = this.getDetectedTimezone();
  readonly timezones = timezoneOptions;
  readonly taskDurationVariabilityOptions = ['low', 'moderate', 'high'];
  readonly arrivalRateTimeUnitOptions: { value: ArrivalRateTimeUnit; label: string }[] = [
    { value: 'seconds', label: 'Seconds' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' }
  ];
  readonly weekdayOptions = [
    { code: 'MON', label: 'Monday', selected: true },
    { code: 'TUE', label: 'Tuesday', selected: true },
    { code: 'WED', label: 'Wednesday', selected: true },
    { code: 'THU', label: 'Thursday', selected: true },
    { code: 'FRI', label: 'Friday', selected: true },
    { code: 'SAT', label: 'Saturday', selected: false },
    { code: 'SUN', label: 'Sunday', selected: false }
  ];
  simulationShifts: SimulationArrivalScheduleShift[] = [
    { start_time: '08:00', end_time: '16:00' }
  ];
  simulationHolidays: string[] = [];
  savedSimulationConfigurations: SavedSimulationConfiguration[] = [];
  selectedSimulationConfigurationId: string | null = null;
  private selectedSimulationConfigurationName: string | null = null;

  ngOnChanges() {
    this.loadSavedSimulationConfigurations();
  }

  ngDoCheck(): void {
    if (!this.dataViewer) return;
    try {
      const serialized = JSON.stringify(this.getCurrentSimulationConfig());
      if (!this.lastObservedSimulationConfig) {
        this.lastObservedSimulationConfig = serialized;
        return;
      }
      if (serialized !== this.lastObservedSimulationConfig) {
        this.lastObservedSimulationConfig = serialized;
        this.markSimulationChanged();
      }
    } catch {
      // The Canvas may be mid-import; it is not a configuration mutation.
    }
  }

  override toggle() {
    super.toggle();

    if (this.isOverviewActive) {
      this.loadSavedSimulationConfigurations();
    }
  }

  private getDetectedTimezone() {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return timezoneOptions.includes(detectedTimezone)
      ? detectedTimezone
      : SimulationOverviewComponent.fallbackTimezone;
  }

  onNextTask(node: AbstractShape){
    node.simulationValues!.nextTaskProb = node.simulationValues!.nextTaskValue.name === nextTaskType[1].name;
  }

  onNoWorkerChange(node: AbstractShape, noWorker: boolean) {
    if (!node.simulationValues) {
      return;
    }

    node.simulationValues.no_worker = noWorker;
    if (!noWorker) {
      node.simulationValues.workersSlider.value = 1;
    }
  }

  getTaskProbabilityValue(step: AbstractShape, relationNodeId: string) {
    return this.getTaskProbabilityField(step, relationNodeId).value;
  }

  setTaskProbabilityValue(step: AbstractShape, relationNodeId: string, value: string | number | null) {
    this.getTaskProbabilityField(step, relationNodeId).value = value == null ? '' : String(value);
  }

  private getTaskProbabilityField(step: AbstractShape, relationNodeId: string): IField {
    const taskProbability = step.simulationValues!.taskProbability;
    const relationStepId = this.getRelationStepId(relationNodeId);
    let field = taskProbability.find((item) => item.name === relationStepId || item.name === relationNodeId);

    if (!field) {
      field = { name: relationStepId, value: '' };
      taskProbability.push(field);
    } else if (field.name !== relationStepId) {
      field.name = relationStepId;
    }

    return field;
  }

  selectNodesForSimulation(){
    for (const [index, shape] of this.currentShapeFocus.getShape().entries()) {
      shape.simulationValues = new SimulationValues(shape, this.dataViewer.updateScreen);
      shape.simulationValues.task_order = this.getSimulationStepCode(index);
    }
    this.simulationNodes.setShapes([...this.currentShapeFocus.getShape()]);
    this.selectedSimulationConfigurationId = null;
    this.selectedSimulationConfigurationName = null;
    this.currentShapeFocus.resetList();
    this.syncSimulationStepRelations();
    this.syncArrivalRateEntries();
  }

  createSimulationStep() {
    const dialogRef = this.dialog.open(SimpleInputDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 48px)',
      data: {
        title: 'New simulation step',
        label: 'Step name',
        placeholder: 'Enter a step name'
      }
    });

    dialogRef.afterClosed().subscribe((name) => {
      if (typeof name !== 'string' || !name.trim()) {
        return;
      }

      const step = this.createSimulationOnlyStep(name.trim());
      step.simulationValues!.task_order = this.getNextSimulationStepId();
      this.simulationNodes.setShapes([...this.simulationNodes.getShape(), step]);
      this.syncSimulationStepRelations();
      this.syncArrivalRateEntries();
      this.dataViewer.updateScreen();
    });
  }

  private createSimulationOnlyStep(name: string, taskUUID = `simulation-step-${generateUUID()}`) {
    const step = new CanvasItem(taskUUID, 0);
    step.name = name;
    step.dbPropierties = {
      __EDT_UUID__: taskUUID,
      name,
      __simulationOnly: true
    };
    step.simulationValues = new SimulationValues(step, this.dataViewer.updateScreen);

    return step;
  }

  recalculateSimulationStepIds() {
    this.simulationNodes.getShape().forEach((step, index) => {
      if (step.simulationValues) {
        step.simulationValues.task_order = this.getSimulationStepCode(index);
      }
    });

    this.syncSimulationStepRelations();
    this.dataViewer.updateScreen();
  }

  startNewSimulationConfiguration() {
    this.openSimulationConfigurationNameDialog(
      'New simulation configuration',
      'Enter a name for the new simulation configuration.',
      this.getNextSimulationConfigurationName(),
      (configurationName) => {
        this.resetSimulationConfiguration();
        this.saveSimulationConfiguration(this.getCurrentSimulationConfig(), configurationName, true);
      }
    );
  }

  copySelectedSimulationConfiguration() {
    const selectedConfiguration = this.savedSimulationConfigurations.find((configuration) => {
      return configuration.simID === this.selectedSimulationConfigurationId;
    });

    if (!selectedConfiguration) {
      return;
    }

    this.openSimulationConfigurationNameDialog(
      'Copy simulation configuration',
      'Enter a name for the copied simulation configuration.',
      `${selectedConfiguration.name} copy`,
      (configurationName) => {
        this.saveSimulationConfiguration(selectedConfiguration.simCofig, configurationName, true);
      }
    );
  }

  exportSelectedSimulationConfiguration() {
    const selectedConfiguration = this.savedSimulationConfigurations.find((configuration) => {
      return configuration.simID === this.selectedSimulationConfigurationId;
    });

    if (!selectedConfiguration) {
      return;
    }

    const jsonBlob = new Blob([
      JSON.stringify({
        model_UUID: this.getCurrentModelUUID(),
        name: selectedConfiguration.name,
        simConfig: selectedConfiguration.simCofig
      }, null, 2)
    ], { type: 'application/json' });
    const safeName = selectedConfiguration.name.replace(/[^a-z0-9-_]+/gi, '_');
    const modelName = this.dataViewer.savedDocument.name_value.replace(/[^a-z0-9-_]+/gi, '_');
    FileSaver.saveAs(jsonBlob, `DXC_TWS_SIMC_[${modelName}]_${safeName  || 'simulation_configuration'}.json`);
  }

  importSimulationConfiguration(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const importedConfiguration = this.parseImportedSimulationConfiguration(
          JSON.parse(reader.result as string),
          file.name
        );

        this.openSimulationConfigurationNameDialog(
          'Import simulation configuration',
          'Enter a name for the imported simulation configuration.',
          importedConfiguration.name,
          (configurationName) => {
            this.saveSimulationConfiguration(
              importedConfiguration.simConfig,
              configurationName,
              true
            );
          }
        );
      } catch (error) {
        this.dialog.open(NotifyDialogComponent, {
          width: '420px',
          data: {
            title: 'Import simulation configuration',
            content: 'Invalid JSON file: ' + error
          }
        });
      } finally {
        input.value = '';
      }
    };

    reader.onerror = () => {
      this.dialog.open(NotifyDialogComponent, {
        width: '420px',
        data: {
          title: 'Import simulation configuration',
          content: 'File reading error: ' + reader.error
        }
      });
      input.value = '';
    };

    reader.readAsText(file);
  }

  saveCurrentSimulationConfiguration() {
    if (!this.simulationNodes.getShape().length) {
      return;
    }

    if (this.selectedSimulationConfigurationId) {
      this.saveSimulationConfiguration(this.getCurrentSimulationConfig());
      return;
    }

    this.openSimulationConfigurationNameDialog(
      'Save simulation configuration',
      'Enter a name for the simulation configuration.',
      this.getNextSimulationConfigurationName(),
      (configurationName) => {
        this.saveSimulationConfiguration(this.getCurrentSimulationConfig(), configurationName, true);
      }
    );
  }

  applySelectedSimulationConfiguration() {
    this.restoreSimulationConfiguration(this.selectedSimulationConfigurationId);
  }

  deleteSelectedSimulationConfiguration() {
    if (!this.selectedSimulationConfigurationId) {
      return;
    }

    const selectedConfiguration = this.savedSimulationConfigurations.find((configuration) => {
      return configuration.simID === this.selectedSimulationConfigurationId;
    });

    if (!selectedConfiguration) {
      return;
    }

    this.dialog.open(NotifyDialogComponent, {
      width: '420px',
      data: {
        title: 'Delete simulation configuration',
        content: `Are you sure you want to delete "${selectedConfiguration.name}"?`,
        isConfirm: true,
        confirmCallback: () => {
          this.deleteSimulationConfiguration(selectedConfiguration.simID);
        }
      }
    });
  }

  private deleteSimulationConfiguration(simID: string) {
    this.simulationService.remove_simulation_config(simID).subscribe({
      next: () => {
        if (this.selectedSimulationConfigurationId === simID) {
          this.selectedSimulationConfigurationId = null;
          this.selectedSimulationConfigurationName = null;
        }

        this.savedSimulationConfigurations = this.savedSimulationConfigurations.filter((configuration) => {
          return configuration.simID !== simID;
        });
        this.loadSavedSimulationConfigurations();
      },
      error: () => {
        this.dialog.open(NotifyDialogComponent, {
          width: '420px',
          data: {
            title: 'Delete simulation configuration',
            content: 'The simulation configuration could not be deleted.'
          }
        });
      }
    });
  }

  restoreSimulationConfiguration(simID: string | null) {
    const savedConfiguration = this.savedSimulationConfigurations.find((configuration) => configuration.simID === simID);

    if (!savedConfiguration) {
      return;
    }

    this.restoreSimulationConfigurationValues(savedConfiguration.simCofig, simID, savedConfiguration.name);
  }

  /** Typed bridge entrypoint. It intentionally reuses the existing restore path. */
  applyValidatedConfiguration(config: SimulationSendValues, expectedRevision: number): void {
    if (expectedRevision !== this.simulationRevision) {
      throw new Error('Simulation configuration changed');
    }
    this.restoreSimulationConfigurationValues(this.cloneSimulationConfig(config), null);
    this.markSimulationChanged();
  }

  getSimulationSnapshot() {
    const configuration = this.cloneSimulationConfig(this.getCurrentSimulationConfig());
    const freshness = !this.lastRun ? undefined : !this.lastRun.executedConfigurationFingerprint
      ? 'UNKNOWN' as const : (this.lastRun.executedConfigurationFingerprint === simulationConfigurationFingerprint(configuration)
          && (this.lastRun.source === 'IMPORT' || this.lastRun.executedSimulationRevision === this.simulationRevision) ? 'CURRENT' as const : 'STALE' as const);
    return {
      revision: this.simulationRevision,
      dirty: this.simulationDirty,
      selectedSimulationId: this.selectedSimulationConfigurationId,
      selectedSimulationName: this.selectedSimulationConfigurationName,
      modelUuid: this.getCurrentModelUUID(),
      configuration, hasLastResult: !!this.lastRun,
      ...(this.lastRun ? { resultSource: this.lastRun.source, resultFreshness: freshness,
        executedAt: this.lastRun.executedAt, executedSimulationRevision: this.lastRun.executedSimulationRevision,
        result: { ...this.lastRun, freshness: freshness! } } : {})
    };
  }

  runCurrentConfiguration(): Promise<void> {
    return this.sendSimulation();
  }

  loadConfigurationForAgent(simulationUuid: string): Promise<void> {
    return new Promise((resolve, reject) => this.simulationService.get_simulation_config(simulationUuid).subscribe({
      next: response => {
        const raw = Array.isArray(response.result) ? response.result[0] : response.result;
        const saved = raw && this.mapSavedSimulationConfiguration(raw as SimulationSavedConfig, 0);
        if (!saved) { reject(new Error('Saved simulation is unavailable')); return; }
        this.restoreSimulationConfigurationValues(saved.simCofig, saved.simID, saved.name);
        this.markSimulationChanged();
        if (!this.isOverviewActive) this.toggle();
        resolve();
      }, error: error => reject(error)
    }));
  }

  private restoreSimulationConfigurationValues(config: SimulationSendValues, selectedConfigurationId: string | null,
      selectedConfigurationName: string | null = null) {
    this.selectedSimulationConfigurationId = selectedConfigurationId;
    this.selectedSimulationConfigurationName = selectedConfigurationId ? selectedConfigurationName : null;
    this.simulationIterations = config.iterations;
    this.simulationPeriodMinutes = config.simulation_period_minutes;
    this.simulationSeed = config.seed;
    this.simulationStartDatetime = config.simulation_start_datetime?.slice(0, 16) || this.simulationStartDatetime;
    this.simulationTimezone = config.simulation_timezone || this.simulationTimezone;
    this.simulationResourcesStructure = (config.resources_structure || []).map((resource) => {
      const editableResource = {
        ...resource,
        interruptions: resource.interruptions?.map((interruption) => ({ ...interruption })) || [],
        items: (resource.items || []).map((item) => typeof item === 'string' ? { item_name: item } : { ...item })
      };

      this.syncSimulationResourceItemsWithCapacity(editableResource);

      return editableResource;
    });
    this.simulationArrivalRateEntries = Object.entries(config.arrival_rate_dic || {}).map(([nodeId, rate]) => {
      const node = this.findCanvasNodeByDatabaseId(nodeId);

      return {
        nodeId: node?.dbPropierties?.__EDT_UUID__ || nodeId,
        ...this.getArrivalRateIntervalFromRate(rate)
      };
    });
    this.weekdayOptions.forEach((weekday) => {
      weekday.selected = config.arrival_schedule?.workdays?.includes(weekday.code) ?? false;
    });
    this.simulationShifts = (config.arrival_schedule?.shifts || []).map((shift) => ({ ...shift }));
    this.simulationHolidays = [...(config.arrival_schedule?.holidays || [])];

    const nodes = config.tasks_structure
      .map((task) => this.findSimulationTaskNode(task)
        ?? (this.isSimulationOnlyStepId(task.task_UUID)
          ? this.createSimulationOnlyStep(task.task_name, task.task_UUID)
          : undefined))
      .filter((node): node is AbstractShape => !!node);

    nodes.forEach((node) => {
      const task = config.tasks_structure.find((item) => this.isSimulationTaskNode(node, item));
      node.simulationValues = new SimulationValues(node, this.dataViewer.updateScreen);

      if (!task) {
        return;
      }

      node.simulationValues.task_order = task.task_order;
      node.simulationValues.task_name = task.task_name;
      node.simulationValues.task_duration_avg = task.task_duration_avg;
      node.simulationValues.task_duration_stdev = task.task_duration_stdev;
      node.simulationValues.task_duration_variability = task.task_duration_variability;
      node.simulationValues.no_worker = task.workers == null;
      node.simulationValues.workersSlider.value = task.workers ?? 1;
      node.simulationValues.resource_id = task.resource_id;
      node.simulationValues.resource_units = task.resource_units ?? 1;
      node.simulationValues.queue_priority = task.queue_priority ?? 100;
      node.simulationValues.allow_abandon = task.allow_abandon;
      node.simulationValues.waitSlider.value = task.max_wait_time;
      node.simulationValues.previusTaskValue = previusTaskDepencency.find((option) => option.name === task.previous_task_dependency_type) || previusTaskDepencency[0];
      node.simulationValues.nextTaskValue = nextTaskType.find((option) => option.name === task.next_task_type) || nextTaskType[0];
      node.simulationValues.nextTaskProb = node.simulationValues.nextTaskValue.name === nextTaskType[1].name;
      node.simulationValues.is_terminal = task.is_terminal;
      node.simulationValues.group = task.group || '';
      node.simulationValues.group_assignation = task.group_assignation || '';
    });

    this.simulationNodes.setShapes(nodes);
    this.syncSimulationStepRelations();
    this.restoreSimulationRelations(config);
    this.syncArrivalRateEntries();
    if (selectedConfigurationId) {
      this.lastPersistedSimulationConfig = JSON.stringify(this.getCurrentSimulationConfig());
      this.simulationDirty = false;
    }
  }

  private findSimulationTaskNode(task: SimulationNodeBlock) {
    return this.findCanvasNodeByDatabaseId(task.task_UUID);
  }

  private isSimulationOnlyStepId(taskUUID: string) {
    return taskUUID.startsWith('simulation-step-');
  }

  private findCanvasNodeByDatabaseId(id: string) {
    return this.findCanvasNode((node) => node.dbPropierties?.__EDT_UUID__ === id);
  }

  private findCanvasNode(predicate: (node: AbstractShape) => boolean, nodes: AbstractShape[] = this.dataViewer.canvasItems): AbstractShape | undefined {
    for (const node of nodes) {
      if (predicate(node)) {
        return node;
      }

      const childNode = this.findCanvasNode(predicate, node.children || []);

      if (childNode) {
        return childNode;
      }
    }

    return undefined;
  }

  private isSimulationTaskNode(node: AbstractShape, task: SimulationNodeBlock) {
    return node.dbPropierties?.__EDT_UUID__ === task.task_UUID;
  }

  reorderSimulationSteps(event: CdkDragDrop<AbstractShape[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const reorderedSteps = [...this.simulationNodes.getShape()];
    moveItemInArray(reorderedSteps, event.previousIndex, event.currentIndex);
    this.simulationNodes.setShapes(reorderedSteps);
    this.syncSimulationStepRelations();
    this.syncArrivalRateEntries();
  }

  getSimulationStepCode(index: number) {
    return (index + 1) * this.simulationStepIncrement;
  }

  private getNextSimulationStepId() {
    const currentStepIds = this.simulationNodes.getShape()
      .map((step) => step.simulationValues?.task_order)
      .filter((stepId): stepId is number => Number.isFinite(stepId));

    return currentStepIds.length
      ? Math.max(...currentStepIds) + this.simulationStepIncrement
      : this.simulationStepIncrement;
  }

  getSimulationStepOrder(step: AbstractShape, index: number) {
    return step.simulationValues?.task_order ?? this.getSimulationStepCode(index);
  }

  getSimulationStepCodeByNodeId(nodeId: string) {
    return this.stepCodeByNodeId[nodeId] ?? null;
  }

  getRelationStepId(nodeId: string) {
    const stepCode = this.getSimulationStepCodeByNodeId(nodeId);
    return stepCode == null ? nodeId : String(stepCode);
  }

  getNodeNameByID(nodeId: string){
    return this.simulationNodes.getShape().find((node) => {
      return node.dbPropierties.__EDT_UUID__ === nodeId;
    });
  }

  getRelationNodeIds(stepId: string, direction: SimulationStepDirection) {
    const relations = this.stepRelations[stepId];
    if (!relations) {
      return [];
    }

    const autoNodeId = direction === 'previous' ? relations.previousAutoNodeId : relations.nextAutoNodeId;
    const autoHidden = direction === 'previous' ? relations.previousAutoHidden : relations.nextAutoHidden;
    const manualNodeIds = direction === 'previous' ? relations.previousManualNodeIds : relations.nextManualNodeIds;

    return autoNodeId && !autoHidden ? [autoNodeId, ...manualNodeIds] : [...manualNodeIds];
  }

  getAvailableRelationOptions(stepId: string, direction: SimulationStepDirection) {
    const selectedNodeIds = new Set(this.getRelationNodeIds(stepId, direction));

    return this.simulationNodes.getShape().filter((node) => {
      return node.dbPropierties.__EDT_UUID__ !== stepId && !selectedNodeIds.has(node.dbPropierties.__EDT_UUID__);
    });
  }

  onRelationSelectionChange(stepId: string, direction: SimulationStepDirection, nodeId: string | null) {
    const relations = this.stepRelations[stepId];
    if (!relations) {
      return;
    }

    if (direction === 'previous') {
      relations.selectedPreviousNodeId = nodeId;
      return;
    }

    relations.selectedNextNodeId = nodeId;
  }

  addRelationNode(stepId: string, direction: SimulationStepDirection) {
    const relations = this.stepRelations[stepId];
    if (!relations) {
      return;
    }

    const selectedNodeId = direction === 'previous' ? relations.selectedPreviousNodeId : relations.selectedNextNodeId;
    if (!selectedNodeId || this.getRelationNodeIds(stepId, direction).includes(selectedNodeId)) {
      return;
    }

    if (direction === 'previous') {
      relations.previousManualNodeIds = [...relations.previousManualNodeIds, selectedNodeId];
      relations.selectedPreviousNodeId = null;
      this.syncStepSimulationValues(stepId);
      return;
    }

    relations.nextManualNodeIds = [...relations.nextManualNodeIds, selectedNodeId];
    relations.selectedNextNodeId = null;
    this.syncStepSimulationValues(stepId);
  }

  removeRelationNode(stepId: string, direction: SimulationStepDirection, nodeId: string) {
    const relations = this.stepRelations[stepId];
    if (!relations) {
      return;
    }

    if (direction === 'previous') {
      if (relations.previousAutoNodeId === nodeId && !relations.previousAutoHidden) {
        relations.previousAutoHidden = true;
        this.syncStepSimulationValues(stepId);
        return;
      }

      relations.previousManualNodeIds = relations.previousManualNodeIds.filter((id) => id !== nodeId);
      this.syncStepSimulationValues(stepId);
      return;
    }

    if (relations.nextAutoNodeId === nodeId && !relations.nextAutoHidden) {
      relations.nextAutoHidden = true;
      this.syncStepSimulationValues(stepId);
      return;
    }

    relations.nextManualNodeIds = relations.nextManualNodeIds.filter((id) => id !== nodeId);
    this.syncStepSimulationValues(stepId);
  }

  private syncStepSimulationValues(stepId: string) {
    const relations = this.stepRelations[stepId];
    const step = this.simulationNodes.getShape().find((simulationStep) => {
      return simulationStep.dbPropierties.__EDT_UUID__ === stepId;
    });

    if (!relations || !step?.simulationValues) {
      return;
    }

    this.syncTaskProbabilityFields(step);
    step.simulationValues.previous_task = this.getRelationTaskOrders(relations, 'previous');
    step.simulationValues.next_task = this.getRelationTaskOrders(relations, 'next');
  }

  private syncSimulationStepRelations() {
    const simulationSteps = this.simulationNodes.getShape();
    const availableNodeIds = new Set(simulationSteps.map((step) => step.dbPropierties.__EDT_UUID__));
    const nextRelations: Record<string, SimulationStepRelations> = {};
    const previousStepCodeByNodeId = { ...this.stepCodeByNodeId };

    this.stepCodeByNodeId = {};
    simulationSteps.forEach((step, index) => {
      const taskOrder = step.simulationValues?.task_order ?? this.getSimulationStepCode(index);
      this.stepCodeByNodeId[step.dbPropierties.__EDT_UUID__] = taskOrder;
    });

    simulationSteps.forEach((step, index) => {
      const existingRelations = this.stepRelations[step.dbPropierties.__EDT_UUID__];
      const previousAutoNodeId = simulationSteps[index - 1]?.dbPropierties.__EDT_UUID__ ?? null;
      const nextAutoNodeId = simulationSteps[index + 1]?.dbPropierties.__EDT_UUID__ ?? null;

      nextRelations[step.dbPropierties.__EDT_UUID__] = {
        previousAutoNodeId,
        nextAutoNodeId,
        previousAutoHidden: existingRelations?.previousAutoNodeId === previousAutoNodeId ? !!existingRelations.previousAutoHidden : false,
        nextAutoHidden: existingRelations?.nextAutoNodeId === nextAutoNodeId ? !!existingRelations.nextAutoHidden : false,
        previousManualNodeIds: this.sanitizeManualNodeIds(
          existingRelations?.previousManualNodeIds ?? [],
          availableNodeIds,
          step.dbPropierties.__EDT_UUID__,
          previousAutoNodeId
        ),
        nextManualNodeIds: this.sanitizeManualNodeIds(
          existingRelations?.nextManualNodeIds ?? [],
          availableNodeIds,
          step.dbPropierties.__EDT_UUID__,
          nextAutoNodeId
        ),
        selectedPreviousNodeId: this.sanitizeSelectedNodeId(
          existingRelations?.selectedPreviousNodeId ?? null,
          availableNodeIds,
          step.dbPropierties.__EDT_UUID__,
          previousAutoNodeId,
          existingRelations?.previousManualNodeIds ?? []
        ),
        selectedNextNodeId: this.sanitizeSelectedNodeId(
          existingRelations?.selectedNextNodeId ?? null,
          availableNodeIds,
          step.dbPropierties.__EDT_UUID__,
          nextAutoNodeId,
          existingRelations?.nextManualNodeIds ?? []
        )
      };
    });

    this.stepRelations = nextRelations;

    simulationSteps.forEach((step) => {
      if (!step.simulationValues) {
        return;
      }

      const relations = nextRelations[step.dbPropierties.__EDT_UUID__];
      this.syncTaskProbabilityFields(step, previousStepCodeByNodeId);
      step.simulationValues.previous_task = this.getRelationTaskOrders(relations, 'previous');
      step.simulationValues.next_task = this.getRelationTaskOrders(relations, 'next');
    });
  }

  private syncTaskProbabilityFields(
    step: AbstractShape,
    previousStepCodeByNodeId: Record<string, number> = this.stepCodeByNodeId
  ) {
    if (!step.simulationValues) {
      return;
    }

    const nextRelationNodeIds = this.getRelationNodeIds(step.dbPropierties.__EDT_UUID__, 'next');
    const nextRelationNodeIdsSet = new Set(nextRelationNodeIds);
    const nextRelationNodeIdByPreviousStepId = new Map<string, string>();

    nextRelationNodeIds.forEach((nodeId) => {
      const previousStepId = previousStepCodeByNodeId[nodeId];
      if (previousStepId != null) {
        nextRelationNodeIdByPreviousStepId.set(String(previousStepId), nodeId);
      }
    });

    const taskProbabilityByStepId = new Map<string, IField>();

    step.simulationValues.taskProbability.forEach((field) => {
      const relationNodeId = nextRelationNodeIdsSet.has(field.name)
        ? field.name
        : nextRelationNodeIdByPreviousStepId.get(field.name);

      if (!relationNodeId) {
        return;
      }

      const relationStepId = this.getRelationStepId(relationNodeId);
      if (!taskProbabilityByStepId.has(relationStepId)) {
        taskProbabilityByStepId.set(relationStepId, { ...field, name: relationStepId });
      }
    });

    nextRelationNodeIds.forEach((relationNodeId) => {
      const relationStepId = this.getRelationStepId(relationNodeId);
      if (!taskProbabilityByStepId.has(relationStepId)) {
        taskProbabilityByStepId.set(relationStepId, { name: relationStepId, value: '' });
      }
    });

    step.simulationValues.taskProbability = Array.from(taskProbabilityByStepId.values());
  }

  private getRelationTaskOrders(relations: SimulationStepRelations, direction: SimulationStepDirection) {
    const autoNodeId = direction === 'previous' ? relations.previousAutoNodeId : relations.nextAutoNodeId;
    const autoHidden = direction === 'previous' ? relations.previousAutoHidden : relations.nextAutoHidden;
    const manualNodeIds = direction === 'previous' ? relations.previousManualNodeIds : relations.nextManualNodeIds;
    const nodeIds = autoNodeId && !autoHidden ? [autoNodeId, ...manualNodeIds] : manualNodeIds;

    return nodeIds
      .map((nodeId) => this.getSimulationStepCodeByNodeId(nodeId))
      .filter((taskOrder): taskOrder is number => taskOrder !== null);
  }

  private sanitizeManualNodeIds(
    nodeIds: string[],
    availableNodeIds: Set<string>,
    currentStepId: string,
    autoNodeId: string | null
  ) {
    const seenNodeIds = new Set<string>();

    return nodeIds.filter((nodeId) => {
      if (
        !availableNodeIds.has(nodeId) ||
        nodeId === currentStepId ||
        nodeId === autoNodeId ||
        seenNodeIds.has(nodeId)
      ) {
        return false;
      }

      seenNodeIds.add(nodeId);
      return true;
    });
  }

  private sanitizeSelectedNodeId(
    nodeId: string | null,
    availableNodeIds: Set<string>,
    currentStepId: string,
    autoNodeId: string | null,
    manualNodeIds: string[]
  ) {
    if (
      !nodeId ||
      !availableNodeIds.has(nodeId) ||
      nodeId === currentStepId ||
      nodeId === autoNodeId ||
      manualNodeIds.includes(nodeId)
    ) {
      return null;
    }

    return nodeId;
  }

  addSimulationShift() {
    this.simulationShifts = [...this.simulationShifts, { start_time: '08:00', end_time: '16:00' }];
  }

  removeSimulationShift(index: number) {
    if (this.simulationShifts.length === 1) {
      return;
    }

    this.simulationShifts = this.simulationShifts.filter((_, shiftIndex) => shiftIndex !== index);
  }

  addSimulationHoliday() {
    this.simulationHolidays = [...this.simulationHolidays, ''];
  }

  removeSimulationHoliday(index: number) {
    this.simulationHolidays = this.simulationHolidays.filter((_, holidayIndex) => holidayIndex !== index);
  }

  addSimulationResource() {
    const resourceIndex = this.simulationResourcesStructure.length + 1;

    this.simulationResourcesStructure = [
      ...this.simulationResourcesStructure,
      {
        resource_id: `resource_${resourceIndex}`,
        resource_name: `Resource ${resourceIndex}`,
        capacity: 1,
        interruptions: [],
        items: [{ item_name: 'Item 1' }]
      }
    ];
  }

  removeSimulationResource(index: number) {
    this.simulationResourcesStructure = this.simulationResourcesStructure.filter((_, resourceIndex) => resourceIndex !== index);
  }

  setSimulationStepResource(simulationValues: SimulationValues, resourceId: string | null) {
    simulationValues.resource_id = resourceId;
    simulationValues.group = resourceId || '';
  }

  setSimulationResourceCapacity(resource: EditableSimulationResourceStructure, value: string | number | null) {
    if (value === '' || value == null) {
      return;
    }

    const nextCapacity = this.getNormalizedResourceCapacity(value);

    if (nextCapacity == null) {
      return;
    }

    resource.capacity = nextCapacity;
    this.syncSimulationResourceItemsWithCapacity(resource);
  }

  private syncSimulationResourceItemsWithCapacity(resource: EditableSimulationResourceStructure) {
    const capacity = this.getNormalizedResourceCapacity(resource.capacity);
    if (capacity == null) {
      return;
    }

    resource.capacity = capacity;
    const items = resource.items || [];

    if (items.length > capacity) {
      resource.items = items.slice(0, capacity);
      return;
    }

    resource.items = [
      ...items,
      ...Array.from({ length: capacity - items.length }, (_, index) => ({
        item_name: `Item ${items.length + index + 1}`
      }))
    ];
  }

  private getNormalizedResourceCapacity(value: string | number | null) {
    if (value === '' || value == null) {
      return null;
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue) && numericValue >= 0
      ? Math.floor(numericValue)
      : null;
  }

  addSimulationResourceInterruption(resource: EditableSimulationResourceStructure) {
    const interruption: SimulationResourceInterruption = {
      type: 'recurring',
      every_minutes: 60,
      duration_minutes: 10,
      reason: ''
    };

    resource.interruptions = [...resource.interruptions || [], interruption];
  }

  removeSimulationResourceInterruption(resource: EditableSimulationResourceStructure, index: number) {
    resource.interruptions = resource.interruptions?.filter((_, interruptionIndex) => interruptionIndex !== index);
  }

  addArrivalRateEntry() {
    const selectedNodeIds = new Set(this.simulationArrivalRateEntries.map((entry) => entry.nodeId));
    const nextNode = this.simulationNodes.getShape().find((node) => !selectedNodeIds.has(node.dbPropierties.__EDT_UUID__));

    this.simulationArrivalRateEntries = [
      ...this.simulationArrivalRateEntries,
      { nodeId: nextNode?.dbPropierties.__EDT_UUID__ ?? null, arrivals: 1, intervalValue: 1, intervalUnit: 'minutes' }
    ];
  }

  removeArrivalRateEntry(index: number) {
    this.simulationArrivalRateEntries = this.simulationArrivalRateEntries.filter((_, entryIndex) => entryIndex !== index);
  }

  private syncArrivalRateEntries() {
    const simulationNodeIds = new Set(this.simulationNodes.getShape().map((node) => node.dbPropierties.__EDT_UUID__));
    const nextEntries = this.simulationArrivalRateEntries.filter((entry) => entry.nodeId && simulationNodeIds.has(entry.nodeId));

    // An arrival rate is operational evidence, not a visual default. Choosing
    // the first rendered shape can select a Schema/area container and silently
    // turn it into a process entry.
    this.simulationArrivalRateEntries = nextEntries;
  }

  getArrivalRateForEntry(entry: SimulationArrivalRateEntry) {
    const intervalMinutes = this.getArrivalRateIntervalMinutes(entry);
    const arrivals = Number(entry.arrivals);

    return intervalMinutes > 0 && Number.isFinite(arrivals) && arrivals > 0
      ? arrivals / intervalMinutes
      : 0;
  }

  private getArrivalRateIntervalMinutes(entry: SimulationArrivalRateEntry) {
    const intervalValue = Number(entry.intervalValue);

    if (!Number.isFinite(intervalValue) || intervalValue <= 0) {
      return 0;
    }

    switch (entry.intervalUnit) {
      case 'seconds':
        return intervalValue / 60;
      case 'hours':
        return intervalValue * 60;
      case 'minutes':
      default:
        return intervalValue;
    }
  }

  private getArrivalRateIntervalFromRate(rate: number) {
    const numericRate = Number(rate);

    return {
      arrivals: 1,
      intervalValue: Number.isFinite(numericRate) && numericRate > 0
        ? 1 / numericRate
        : 0,
      intervalUnit: 'minutes' as ArrivalRateTimeUnit
    };
  }

  private getSimulationStartDatetime() {
    return this.simulationStartDatetime.length === 16
      ? `${this.simulationStartDatetime}:00`
      : this.simulationStartDatetime;
  }

  private getResourcesStructure(): SimulationResourceStructure[] {
    return this.simulationResourcesStructure.map((resource) => ({
      ...resource,
      capacity: Number(resource.capacity),
      interruptions: resource.interruptions?.map((interruption) => ({
        ...interruption,
        every_minutes: Number(interruption.every_minutes),
        duration_minutes: Number(interruption.duration_minutes)
      })),
      items: resource.items.map((item) => (item.item_name)) //modify with interfacing options
    }));
  }

  private getArrivalRateDic() {
    return this.simulationArrivalRateEntries.reduce<Record<string, number>>((arrivalRateDic, entry) => {
      if (entry.nodeId) {
        arrivalRateDic[entry.nodeId] = this.getArrivalRateForEntry(entry);
      }

      return arrivalRateDic;
    }, {});
  }

  private getCurrentModelUUID() {
    return this.dataViewer?.savedDocument?.model_UUID || '';
  }

  private loadSavedSimulationConfigurations(simulationName?: string, preserveConfirmedSelection = false) {
    const modelUUID = this.getCurrentModelUUID();

    if (!modelUUID) {
      this.savedSimulationConfigurations = [];
      this.selectedSimulationConfigurationId = null;
      this.selectedSimulationConfigurationName = null;
      return;
    }

    this.simulationService.list_simulation_config(modelUUID, simulationName).subscribe({
      next: (response: ServerResponse<SimulationSavedConfig>) => {
        const configurations = Array.isArray(response.result) ? response.result : [];
        this.savedSimulationConfigurations = configurations
          .map((configuration, index) => this.mapSavedSimulationConfiguration(configuration, index))
          .filter((configuration): configuration is SavedSimulationConfiguration => !!configuration);

        if (
          this.selectedSimulationConfigurationId &&
          !this.savedSimulationConfigurations.some((configuration) => configuration.simID === this.selectedSimulationConfigurationId) &&
          !preserveConfirmedSelection
        ) {
          this.selectedSimulationConfigurationId = null;
          this.selectedSimulationConfigurationName = null;
        } else if (this.selectedSimulationConfigurationId) {
          this.selectedSimulationConfigurationName = this.savedSimulationConfigurations.find(
            configuration => configuration.simID === this.selectedSimulationConfigurationId)?.name
            || this.selectedSimulationConfigurationName;
        }
      },
      error: () => {
        this.savedSimulationConfigurations = [];
        if (!preserveConfirmedSelection) {
          this.selectedSimulationConfigurationId = null;
          this.selectedSimulationConfigurationName = null;
        }
      }
    });
  }

  private getNextSimulationConfigurationName() {
    return `Simulation configuration ${this.savedSimulationConfigurations.length + 1}`;
  }

  private mapSavedSimulationConfiguration(
    configuration: SimulationSavedConfig,
    index: number
  ): SavedSimulationConfiguration | null {
    const simCofig = this.parseSavedSimulationStructure(configuration.simulation_structure);

    if (!configuration.simulation_UUID || !simCofig) {
      return null;
    }

    return {
      model_UUID: this.getCurrentModelUUID(),
      simID: configuration.simulation_UUID,
      name: configuration.simulation_name || `Simulation configuration ${index + 1}`,
      simCofig
    };
  }

  private parseSavedSimulationStructure(simulationStructure: string | SimulationSendValues) {
    if (typeof simulationStructure !== 'string') {
      return simulationStructure;
    }

    try {
      return JSON.parse(simulationStructure) as SimulationSendValues;
    } catch {
      return null;
    }
  }

  private parseImportedSimulationConfiguration(
    importedValue: SimulationSendValues | ImportedSimulationConfiguration,
    fileName: string
  ): Required<ImportedSimulationConfiguration> {
    const importedConfig = importedValue as ImportedSimulationConfiguration;
    const simConfig = importedConfig.simConfig || importedValue as SimulationSendValues;
    const currentModelUUID = this.getCurrentModelUUID();

    if (!this.isSimulationSendValues(simConfig)) {
      throw new Error('The file does not contain a simulation configuration.');
    }

    if (!importedConfig.model_UUID) {
      throw new Error('The file does not contain a model UUID.');
    }

    if (importedConfig.model_UUID !== currentModelUUID) {
      throw new Error('The simulation configuration belongs to a different model.');
    }

    return {
      model_UUID: importedConfig.model_UUID,
      name: importedConfig.name || this.getSimulationConfigurationNameFromFile(fileName),
      simConfig
    };
  }

  private getSimulationConfigurationNameFromFile(fileName: string) {
    return fileName.replace(/\.json$/i, '').trim() || this.getNextSimulationConfigurationName();
  }

  private isSimulationSendValues(value: unknown): value is SimulationSendValues {
    const config = value as SimulationSendValues;

    return !!config
      && typeof config === 'object'
      && typeof config.iterations === 'number'
      && typeof config.simulation_period_minutes === 'number'
      && Array.isArray(config.resources_structure)
      && Array.isArray(config.tasks_structure)
      && !!config.arrival_rate_dic
      && typeof config.arrival_rate_dic === 'object';
  }

  private openSimulationConfigurationNameDialog(
    title: string,
    _content: string,
    defaultName: string,
    onNameSelected: (name: string) => void
  ) {
    const dialogRef = this.dialog.open(SimpleInputDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 48px)',
      panelClass: 'simulation-config-name-dialog',
      data: {
        title,
        label: 'Configuration name',
        placeholder: 'Enter a configuration name',
        value: defaultName
      }
    });

    dialogRef.afterClosed().subscribe((name) => {
      if (typeof name !== 'string') {
        return;
      }

      const trimmedName = name.trim();

      if (!trimmedName) {
        return;
      }

      onNameSelected(trimmedName);
    });
  }

  private resetSimulationConfiguration() {
    this.resetSimulationValuesForNodes(this.dataViewer?.canvasItems || []);
    this.selectedSimulationConfigurationId = null;
    this.selectedSimulationConfigurationName = null;
    this.simulationReturnValues = undefined;
    this.simulationNodes.setShapes([]);
    this.stepRelations = {};
    this.stepCodeByNodeId = {};
    this.simulationIterations = 10;
    this.simulationPeriodMinutes = 480;
    this.simulationResourcesStructure = [];
    this.simulationArrivalRateEntries = [];
    this.simulationSeed = 2;
    this.simulationStartDatetime = '2026-05-11T08:00';
    this.simulationTimezone = this.getDetectedTimezone();
    this.weekdayOptions.forEach((weekday) => {
      weekday.selected = ['MON', 'TUE', 'WED', 'THU', 'FRI'].includes(weekday.code);
    });
    this.simulationShifts = [{ start_time: '08:00', end_time: '16:00' }];
    this.simulationHolidays = [];
    this.currentShapeFocus?.resetList();
  }

  private resetSimulationValuesForNodes(nodes: AbstractShape[]) {
    nodes.forEach((node) => {
      node.simulationValues = null;
      node.simulationAverage = null;
      this.resetSimulationValuesForNodes(node.children || []);
    });
  }

  private getUniqueSimulationConfigurationName(name: string, excludedSimID?: string) {
    const modelUUID = this.getCurrentModelUUID();
    const normalizedName = name.trim().toLowerCase();
    const isNameTaken = (configurationName: string) => {
      return this.savedSimulationConfigurations.some((configuration) => {
        return configuration.model_UUID === modelUUID
          && configuration.simID !== excludedSimID
          && configuration.name?.trim().toLowerCase() === configurationName.trim().toLowerCase();
      });
    };

    if (!isNameTaken(normalizedName)) {
      return name;
    }

    let copyIndex = 2;
    let nextName = `${name} ${copyIndex}`;

    while (isNameTaken(nextName)) {
      copyIndex += 1;
      nextName = `${name} ${copyIndex}`;
    }

    return nextName;
  }

  private saveSimulationConfiguration(
    simConfig: SimulationSendValues,
    configurationName?: string,
    createNewConfiguration = false
  ) {
    const modelUUID = this.getCurrentModelUUID();

    if (!modelUUID) {
      return;
    }

    const existingConfiguration = this.savedSimulationConfigurations.find((configuration) => {
      return !createNewConfiguration && configuration.model_UUID === modelUUID && configuration.simID === this.selectedSimulationConfigurationId;
    });
    const simulationUUID = existingConfiguration?.simID ?? null;
    const simulationName = this.getUniqueSimulationConfigurationName(
      configurationName || existingConfiguration?.name || this.getNextSimulationConfigurationName(),
      simulationUUID || undefined
    );
    const simulationStructure = JSON.stringify(simConfig);

    this.simulationService.save_simulation_config(
      simulationStructure,
      simulationName,
      modelUUID,
      simulationUUID || ''
    ).subscribe({
      next: (response: ServerResponseObject<SimulationSavedConfig>) => {
        const savedId = response.status === 200 ? this.savedSimulationId(response) : null;
        if (!savedId) return;
        this.selectedSimulationConfigurationId = savedId;
        this.selectedSimulationConfigurationName = simulationName;
        this.lastPersistedSimulationConfig = JSON.stringify(this.getCurrentSimulationConfig());
        this.simulationDirty = false;
        this.loadSavedSimulationConfigurations(undefined, true);
      }
    });
  }

  private savedSimulationId(response: ServerResponseObject<SimulationSavedConfig>): string | null {
    // The backend returns a list of Cypher records, although older frontend
    // callers typed this endpoint as a single result object.
    const raw: unknown = response.result;
    const first: unknown = Array.isArray(raw) ? raw[0] : raw;
    return first && typeof first === 'object' && typeof (first as { simulation_UUID?: unknown }).simulation_UUID === 'string'
      ? (first as { simulation_UUID: string }).simulation_UUID : null;
  }

  /** Explicit persistence bridge. Never called by apply or run flows. */
  saveCurrentConfigurationForAgent(mode: 'SAVE' | 'SAVE_AS', name?: string): Promise<void> {
    if (mode === 'SAVE_AS' && !name?.trim()) throw new Error('A simulation name is required');
    const modelUUID = this.getCurrentModelUUID();
    if (!modelUUID) throw new Error('Simulation model is unavailable');
    const configuration = this.getCurrentSimulationConfig();
    const serializedConfiguration = JSON.stringify(configuration);
    const existing = mode === 'SAVE' && this.selectedSimulationConfigurationId
        && this.selectedSimulationConfigurationName
      ? { simID: this.selectedSimulationConfigurationId, name: this.selectedSimulationConfigurationName }
      : undefined;
    if (mode === 'SAVE' && !existing) {
      throw new Error('No saved simulation is selected; choose Save As and provide a name');
    }
    const simulationUUID = existing?.simID ?? null;
    const simulationName = this.getUniqueSimulationConfigurationName(
      name || existing?.name || this.getNextSimulationConfigurationName(), simulationUUID || undefined);
    return new Promise<void>((resolve, reject) => {
      this.simulationService.save_simulation_config(serializedConfiguration, simulationName,
        modelUUID, simulationUUID || '').subscribe({
        next: (response: ServerResponseObject<SimulationSavedConfig>) => {
          const savedId = response.status === 200 ? this.savedSimulationId(response) : null;
          if (!savedId) {
            reject(new Error('Simulation save returned no confirmed configuration ID'));
            return;
          }
          this.selectedSimulationConfigurationId = savedId;
          this.selectedSimulationConfigurationName = simulationName;
          this.lastPersistedSimulationConfig = serializedConfiguration;
          this.simulationDirty = JSON.stringify(this.getCurrentSimulationConfig()) !== serializedConfiguration;
          this.loadSavedSimulationConfigurations(undefined, true);
          resolve();
        },
        error: error => reject(error instanceof Error ? error : new Error('Simulation save failed'))
      });
    });
  }

  private getCurrentSimulationConfig(): SimulationSendValues {
    const nodes = this.simulationNodes.getShape();

    return {
      "iterations": Number(this.simulationIterations),
      "simulation_period_minutes": Number(this.simulationPeriodMinutes),
      "resources_structure": this.getResourcesStructure(),
      "tasks_structure": nodes.map(node => node.simulationValues!.parseIntoSendValues()),
      "arrival_rate_dic": this.getArrivalRateDic(),
      "simulation_start_datetime": this.getSimulationStartDatetime(),
      "simulation_timezone": this.simulationTimezone,
      "arrival_schedule": {
                "workdays": this.weekdayOptions
                  .filter((weekday) => weekday.selected)
                  .map((weekday) => weekday.code),
                "shifts": this.simulationShifts,
                "holidays": this.simulationHolidays.filter((holiday) => holiday)
            },
      "show_detail_needed": false,
      "seed": Number(this.simulationSeed)
    };
  }

  private restoreSimulationRelations(config: SimulationSendValues) {
    this.applySimulationRelations(config.tasks_structure);
  }

  private applySimulationRelations(tasks: SimulationTaskRelationValues[]) {
    const nodeIdByTaskOrder = new Map<number, string>();

    this.simulationNodes.getShape().forEach((node) => {
      if (node.simulationValues) {
        nodeIdByTaskOrder.set(node.simulationValues.task_order, node.dbPropierties.__EDT_UUID__);
      }
    });

    tasks.forEach((task) => {
      const step = this.simulationNodes.getShape().find((node) => {
        return node.dbPropierties.__EDT_UUID__ === task.task_UUID;
      });

      if (!step?.simulationValues) {
        return;
      }

      const stepId = step.dbPropierties.__EDT_UUID__;
      const relations = this.stepRelations[stepId];

      if (!relations) {
        return;
      }

      const previousNodeIds = task.previous_task
        .map((taskOrder) => nodeIdByTaskOrder.get(taskOrder))
        .filter((nodeId): nodeId is string => !!nodeId);
      const nextTaskEntries = Array.isArray(task.next_task) ? task.next_task : [];
      const nextNodeIds = nextTaskEntries
        .map((entry) => Array.isArray(entry) ? entry[1] : entry)
        .map((taskOrder) => nodeIdByTaskOrder.get(taskOrder))
        .filter((nodeId): nodeId is string => !!nodeId);

      relations.previousAutoHidden = !relations.previousAutoNodeId || !previousNodeIds.includes(relations.previousAutoNodeId);
      relations.nextAutoHidden = !relations.nextAutoNodeId || !nextNodeIds.includes(relations.nextAutoNodeId);
      relations.previousManualNodeIds = previousNodeIds.filter((nodeId) => nodeId !== relations.previousAutoNodeId);
      relations.nextManualNodeIds = nextNodeIds.filter((nodeId) => nodeId !== relations.nextAutoNodeId);

      if (step.simulationValues.nextTaskProb) {
        step.simulationValues.taskProbability = nextTaskEntries
          .filter((entry): entry is [number, number] => Array.isArray(entry))
          .map(([probability, taskOrder]) => ({
            name: String(taskOrder),
            value: String(probability)
          }));
      }

      this.syncStepSimulationValues(stepId);
    });
  }

  sendSimulation(): Promise<void>{
    // let json: SimulationSendValues = SimulationJson;
    let json: SimulationSendValues = this.getCurrentSimulationConfig();
    const simSendValues = this.cloneSimulationConfig(json);
    
    this.isLoading = true;
    return new Promise((resolve, reject) => this.simulationService.simulate(json).subscribe({
      next: (res: ServerResponseObject<FullSimulationReport>)=>{
        this.applySimulationFullResult(res.result, simSendValues);
        this.lastRun = { source: 'EXECUTION', executedAt: new Date().toISOString(),
          executedSimulationRevision: this.simulationRevision,
          executedConfigurationFingerprint: simulationConfigurationFingerprint(simSendValues),
          digest: buildAgentSimulationResultDigest(res.result) };
        this.openSimulationReportDialog();
        this.isLoading = false;
        resolve();
      },
      error:()=>{
        this.isLoading = false;
        reject(new Error('Simulation execution failed'));
      }
    }));
  }

  private markSimulationChanged(): void {
    this.simulationRevision += 1;
    this.simulationDirty = true;
    this.lastObservedSimulationConfig = JSON.stringify(this.getCurrentSimulationConfig());
  }

  exportSimulationResult() {
    if (!this.simulationFullResult) {
      return;
    }

    const jsonBlob = new Blob([JSON.stringify(this.simulationFullResult, null, 2)], { type: 'application/json' });
    const modelName = this.dataViewer.savedDocument.name_value.replace(/[^a-z0-9-_]+/gi, '_');
    const finalName = `DXC_TWS_SIMR_[${modelName}]_${generateCurrentDate()}_${generateUUID()}.json`;
    FileSaver.saveAs(jsonBlob, finalName);
  }

  downloadSimulationOutputFile(file: SimulationOutputFile) {
    if (!file.path) {
      return;
    }

    this.simulationService.download_file(file.path).subscribe({
      next: (blob) => FileSaver.saveAs(blob, this.getSimulationOutputFileName(file)),
      error: () => {
        this.dialog.open(NotifyDialogComponent, {
          width: '420px',
          data: {
            title: 'Download simulation file',
            content: 'The simulation output file could not be downloaded.'
          }
        });
      }
    });
  }

  getSimulationOutputFileLabel(file: SimulationOutputFile) {
    return file.name.replace(/_/g, ' ').toUpperCase();
  }

  private getSimulationOutputFileName(file: SimulationOutputFile) {
    const pathName = file.path?.split('/').pop();
    return pathName || file.name;
  }

  importSimulationResult(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = this.parseImportedSimulationResult(JSON.parse(reader.result as string));
        this.restoreImportedSimulationResultConfiguration(result.sim_send_values);

        this.applySimulationFullResult(result);
        this.lastRun = { source: 'IMPORT', executedAt: new Date().toISOString(),
          executedConfigurationFingerprint: simulationConfigurationFingerprint(result.sim_send_values),
          digest: buildAgentSimulationResultDigest(result) };
      } catch (error) {
        this.dialog.open(NotifyDialogComponent, {
          width: '420px',
          data: {
            title: 'Import simulation result',
            content: 'Invalid JSON file: ' + error
          }
        });
      } finally {
        input.value = '';
      }
    };

    reader.onerror = () => {
      this.dialog.open(NotifyDialogComponent, {
        width: '420px',
        data: {
          title: 'Import simulation result',
          content: 'File reading error: ' + reader.error
        }
      });
      input.value = '';
    };

    reader.readAsText(file);
  }

  private applySimulationFullResult(result: FullSimulationReport, simSendValues?: SimulationSendValues) {
    const resultSimSendValues = simSendValues || (result as Partial<ImportedSimulationResult>).sim_send_values;

    if (!resultSimSendValues) {
      return;
    }

    this.simulationFullResult = {
      ...result,
      other_files: Array.isArray(result.other_files) ? result.other_files : [],
      model_UUID: this.getSimulationResultModelUUID(result) || this.getCurrentModelUUID(),
      sim_send_values: this.cloneSimulationConfig(resultSimSendValues)
    };
    this.simulationOutputFiles = this.simulationFullResult.other_files.filter((file) => file.name !== 'result_json');
    this.simulationReturnValues = result.simulation_result;
    this.simulationReport = result.simulation_report;
    this.dataViewer.setSimulationValues(result.simulation_result);
    this.assignSimulationValuesToNode(result.simulation_result.GLOBAL_AVERAGES);
  }

  private parseImportedSimulationResult(importedValue: unknown): ImportedSimulationResult {
    if (!this.isFullSimulationReport(importedValue)) {
      throw new Error('The file does not contain a simulation result.');
    }

    const importedModelUUID = this.getSimulationResultModelUUID(importedValue);
    const currentModelUUID = this.getCurrentModelUUID();

    if (!importedModelUUID) {
      throw new Error('The file does not contain a model UUID.');
    }

    if (importedModelUUID !== currentModelUUID) {
      throw new Error('The simulation result belongs to a different model.');
    }

    const simSendValues = (importedValue as Partial<ImportedSimulationResult>).sim_send_values;

    if (!this.isSimulationSendValues(simSendValues)) {
      throw new Error('The file does not contain the simulation configuration used for this result.');
    }

    return {
      ...importedValue,
      model_UUID: importedModelUUID,
      sim_send_values: simSendValues
    };
  }

  private isFullSimulationReport(value: unknown): value is FullSimulationReport {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const result = value as Partial<FullSimulationReport>;
    return typeof result.simulation_report === 'string'
      && !!result.simulation_result
      && Array.isArray(result.simulation_result.GLOBAL_AVERAGES);
  }

  private getSimulationResultModelUUID(result: FullSimulationReport) {
    return (result as Partial<ImportedSimulationResult>).model_UUID || '';
  }

  private restoreImportedSimulationResultConfiguration(config: SimulationSendValues) {
    this.restoreSimulationConfigurationValues(config, null);
  }

  private cloneSimulationConfig(config: SimulationSendValues): SimulationSendValues {
    return JSON.parse(JSON.stringify(config)) as SimulationSendValues;
  }

  public openSimulationReportDialog() {
    if (!this.simulationReport) {
      return;
    }

    this.dialog.open(SimulationReportDialogComponent, {
      data: {
        report: this.simulationReport,
        exportPdf: () => this.dataViewer.exportSimulationPdf()
      },
      panelClass: 'simulation-report-dialog-panel',
      maxWidth: '96vw'
    });
  }

  assignSimulationValuesToNode(values: SimulationGlobalAverage[]){
    if (this.simulationNodes.getShape().length) {
      this.assignGlobalAverageToNodes(this.simulationNodes.getShape() as AbstractShape[], values);
      return;
    }
    let list = values.map((value: SimulationGlobalAverage)=>{
      return value.task_uuid
    });

    let nodes = list.map((id: string)=>{
      return this.findCanvasNodeByDatabaseId(id);
    });
    nodes = nodes.filter(node=>node);
    this.assignGlobalAverageToNodes(nodes as AbstractShape[], values);
  }
  assignGlobalAverageToNodes(nodes: AbstractShape[], values: SimulationGlobalAverage[]){
    for (const node of nodes) {
      node!.simulationAverage = values.find(value => {
        return node!.dbPropierties.__EDT_UUID__ == value.task_uuid
      });
    }
  }
}
